import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShiftsService } from '../services/supabase/shiftsService';
import './recurringShiftsPage.css';

const DAY_ORDER = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function extractTime(datetimeStr) {
  // Returns "HH:MM" from an ISO datetime string
  if (!datetimeStr) return '00:00';
  return datetimeStr.substring(11, 16);
}

function formatTime(hhmm) {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

function groupShifts(shifts) {
  const map = {};
  for (const s of shifts) {
    const id = s.repeating_shift_id;
    if (!id) continue;
    if (!map[id]) {
      map[id] = {
        id,
        client: s.client,
        caretaker: s.caretaker,
        caretakerEmail: s.caretaker_email,
        color: s.color,
        startTime: extractTime(s.start_time),
        endTime: extractTime(s.end_time),
        days: new Set(),
      };
    }
    const day = new Date(s.start_time).toLocaleDateString('en-US', { weekday: 'long' });
    map[id].days.add(day);
  }
  return Object.values(map).map(g => ({
    ...g,
    days: DAY_ORDER.filter(d => g.days.has(d)),
  })).sort((a, b) => a.client.localeCompare(b.client) || a.caretaker.localeCompare(b.caretaker));
}

export default function RecurringShiftsPage({ clientList, caretakerList }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit modal state
  const [editGroup, setEditGroup] = useState(null);
  const [editClient, setEditClient] = useState('');
  const [editCaretaker, setEditCaretaker] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editChangesOn, setEditChangesOn] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete confirmation state
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const shifts = await ShiftsService.getFutureRepeatingShifts();
      setGroups(groupShifts(shifts));
    } catch {
      setError('Failed to load recurring shifts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Edit modal ──────────────────────────────────────────────────────────────
  const openEdit = (group) => {
    setEditGroup(group);
    setEditClient(group.client);
    setEditCaretaker(group.caretaker);
    setEditStartTime(group.startTime);
    setEditEndTime(group.endTime);
    setEditChangesOn('');
    setEditError('');
  };

  const closeEdit = () => { setEditGroup(null); setEditError(''); };

  const handleSaveEdit = async () => {
    if (!editChangesOn) { setEditError('Please select a "Start Changes On" date.'); return; }
    if (editEndTime <= editStartTime) { setEditError('End time must be after start time.'); return; }

    const fromSecs = new Date(editChangesOn).getTime();
    const caretakerObj = caretakerList?.find(c => c.name === editCaretaker);
    const color = caretakerObj?.color ?? editGroup.color;
    const caretakerEmail = caretakerObj?.email ?? editGroup.caretakerEmail;

    setEditSaving(true);
    setEditError('');
    try {
      const fieldUpdates = { client: editClient, caretaker: editCaretaker, caretaker_email: caretakerEmail, color };
      await ShiftsService.updateFutureRepeatingShiftFields(editGroup.id, fromSecs, fieldUpdates);

      if (editStartTime !== editGroup.startTime || editEndTime !== editGroup.endTime) {
        await ShiftsService.updateFutureRepeatingShiftTimes(editGroup.id, fromSecs, editStartTime, editEndTime);
      }

      closeEdit();
      await load();
    } catch {
      setEditError('Failed to save changes. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (groupId) => {
    setDeleting(true);
    try {
      await ShiftsService.deleteFutureShifts(groupId, Date.now());
      setDeleteGroupId(null);
      await load();
    } catch {
      setError('Failed to delete shifts. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const activeClients = (clientList ?? []).filter(c => !c.is_deactivated).sort((a, b) => a.name.localeCompare(b.name));
  const activeCaretakers = (caretakerList ?? []).filter(c => !c.is_deactivated).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="recurringPageDiv">
      <div className="recurringPageHeader">
        <Link to="/" className="backToHomeLink">← Back to Home</Link>
        <h2 className="recurringPageTitle">Recurring Shifts</h2>
      </div>

      <div className="formSep"></div>

      <div className="recurringSection">
        {loading && <p className="recurringLoading">Loading…</p>}
        {error && <p className="recurringError">{error}</p>}

        {!loading && !error && groups.length === 0 && (
          <p className="recurringEmpty">No future recurring shifts found.</p>
        )}

        {!loading && groups.length > 0 && (
          <div className="recurringTableWrapper">
            <table className="recurringTable">
              <thead>
                <tr>
                  <th className="recTh">Client</th>
                  <th className="recTh">Caretaker</th>
                  <th className="recTh">Day(s)</th>
                  <th className="recTh">Start Time</th>
                  <th className="recTh">End Time</th>
                  <th className="recTh recActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(group => (
                  <tr key={group.id} className="recRow">
                    <td className="recTd">{group.client}</td>
                    <td className="recTd">{group.caretaker}</td>
                    <td className="recTd">{group.days.join(', ') || '—'}</td>
                    <td className="recTd">{formatTime(group.startTime)}</td>
                    <td className="recTd">{formatTime(group.endTime)}</td>
                    <td className="recTd recActions">
                      <button className="recEditBtn" onClick={() => openEdit(group)}>Edit</button>
                      <button className="recDeleteBtn" onClick={() => setDeleteGroupId(group.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editGroup && (
        <div className="recModalOverlay" onClick={closeEdit}>
          <div className="recModal" onClick={e => e.stopPropagation()}>
            <h3 className="recModalTitle">Edit Recurring Shift</h3>

            <div className="recModalField">
              <label className="recModalLabel">Client</label>
              <select className="recModalInput" value={editClient} onChange={e => setEditClient(e.target.value)}>
                {activeClients.map(c => <option key={c.docID} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            <div className="recModalField">
              <label className="recModalLabel">Caretaker</label>
              <select className="recModalInput" value={editCaretaker} onChange={e => setEditCaretaker(e.target.value)}>
                {activeCaretakers.map(c => <option key={c.docID} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            <div className="recModalField">
              <label className="recModalLabel">Start Time</label>
              <input type="time" className="recModalInput" value={editStartTime} onChange={e => setEditStartTime(e.target.value)} />
            </div>

            <div className="recModalField">
              <label className="recModalLabel">End Time</label>
              <input type="time" className="recModalInput" value={editEndTime} onChange={e => setEditEndTime(e.target.value)} />
            </div>

            <div className="recModalField">
              <label className="recModalLabel">Start Changes On</label>
              <input type="date" className="recModalInput" value={editChangesOn} onChange={e => setEditChangesOn(e.target.value)} />
              <span className="recModalHint">Changes apply to all instances on and after this date.</span>
            </div>

            {editError && <p className="recModalError">{editError}</p>}

            <div className="recModalActions">
              <button className="recSaveBtn" onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <button className="recCancelBtn" onClick={closeEdit}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteGroupId && (
        <div className="recModalOverlay" onClick={() => setDeleteGroupId(null)}>
          <div className="recModal recDeleteModal" onClick={e => e.stopPropagation()}>
            <h3 className="recModalTitle">Delete Recurring Shift</h3>
            <p className="recDeleteMsg">This will permanently delete all future instances of this recurring shift after today. This cannot be undone.</p>
            <div className="recModalActions">
              <button className="recConfirmDeleteBtn" onClick={() => handleDelete(deleteGroupId)} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, Delete All Future Shifts'}
              </button>
              <button className="recCancelBtn" onClick={() => setDeleteGroupId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
